import React, { useState } from 'react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import api from '../lib/api';
import { scrollToSection } from '../hooks/useSmoothScroll';
import { 
  Mountain,
  Footprints,
  Heart,
  Compass,
  Users,
  Send,
  ChevronDown,
  Sparkles,
  MapPin,
  Clock,
  ArrowRight
} from 'lucide-react';

const ViajesIniciaticos = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    viaje: '',
    mensaje: '',
    consent: false
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.consent) {
      toast.error('Por favor, acepta el consentimiento para continuar');
      return;
    }
    if (!formData.name || !formData.email) {
      toast.error('Por favor, completa nombre y email');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/viajes/solicitud', {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        viaje: formData.viaje,
        mensaje: formData.mensaje,
        consent: formData.consent
      });
      setSubmitted(true);
      toast.success('¡Solicitud enviada! Te contactaré pronto.');
    } catch (error) {
      toast.error('Ha ocurrido un error. Inténtalo de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToForm = (viaje) => {
    setFormData({ ...formData, viaje });
    scrollToSection('solicitud-form');
  };

  const viajes = [
    {
      id: 'montserrat',
      title: 'Montserrat',
      subtitle: 'Peregrinaje Interior',
      duration: '3-4 días',
      status: 'A demanda',
      statusColor: 'bg-[#c9a227]',
      icon: Mountain,
      description: 'Travesía sagrada por los senderos ocultos de la montaña. Caminaremos por rutas menos conocidas, conectando con puntos energéticos y cuevas que he descubierto tras años de exploración personal.',
      highlights: [
        'Senderos sagrados poco transitados',
        'Cuevas y puntos energéticos ocultos',
        'Meditaciones en lugares de poder',
        'Alojamiento en el Monasterio'
      ],
      ideal: 'Personas con búsqueda espiritual y mínima condición física para caminar.'
    },
    {
      id: 'teruel',
      title: 'REINICIA',
      subtitle: 'Descanso Interno · Retiro de verano',
      duration: '19–22 julio 2026',
      status: 'Reservas abiertas',
      statusColor: 'bg-emerald-600',
      icon: Heart,
      description: 'Tres días para salir del ruido, volver al cuerpo y escuchar el silencio dentro de ti. En el Monasterio Santa María de El Olivar (Teruel), bajo uno de los cielos más limpios de España.',
      highlights: [
        'Descanso real del sistema nervioso',
        'Meditaciones y dinámicas terapéuticas guiadas',
        'Observación astronómica con certificación Starlight',
        'Pensión completa monástica · habitaciones restauradas'
      ],
      ideal: 'Para quien lleva tiempo sosteniendo demasiado y necesita volver a sí.',
      ctaSlug: 'reinicia-2026',
    },
    {
      id: 'camino',
      title: 'Camino de Santiago',
      subtitle: 'Travesía del Corazón',
      duration: '7-8 días',
      status: 'A demanda',
      statusColor: 'bg-[#c9a227]',
      icon: Footprints,
      description: 'Un sendero de automaestría donde cada paso te acerca a ti. 111 km desde Sarria, caminando en la frecuencia del chakra corazón, sostenido por el cuarzo blanco bajo tus pies.',
      highlights: [
        'Caminata consciente (111 km)',
        'Círculos de sanación diarios',
        'Trabajo con la energía del camino',
        'Acompañamiento durante todo el proceso'
      ],
      ideal: 'Personas dispuestas a caminar 5 horas diarias.'
    }
  ];

  return (
    <div className="bg-[#f8f6f0] min-h-screen">
      
      {/* ========== HERO SECTION ========== */}
      <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden">
        {/* Single background image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1684717465603-b47bf0e8138f?w=1920&q=80"
            alt="Montañas al amanecer"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center pt-20">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full mb-8">
            <Compass className="w-4 h-4 text-[#c9a227]" />
            <span className="text-white/90 text-sm tracking-wide">Experiencias transformadoras</span>
          </div>
          
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-heading text-white leading-tight mb-6">
            Viajes
            <span className="block text-[#c9a227]">Iniciáticos</span>
          </h1>
          
          <p className="text-xl lg:text-2xl text-white/90 font-light max-w-2xl mx-auto mb-6">
            Travesías sagradas donde el camino exterior 
            se convierte en viaje interior
          </p>

          <p className="text-white/60 max-w-xl mx-auto mb-12 text-sm lg:text-base">
            Experiencias vivas que se adaptan a la energía del grupo. 
            Cada paso, una oportunidad de encuentro contigo mismo.
          </p>

          <button onClick={() => scrollToSection('viajes')} className="inline-block">
            <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8 py-6">
              Descubrir experiencias
              <ChevronDown className="w-4 h-4 ml-2" />
            </Button>
          </button>
        </div>
      </section>

      {/* ========== INTRO ========== */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="w-12 h-[2px] bg-[#c9a227] mx-auto mb-10" />
          
          <p className="text-xl lg:text-2xl text-[#2D3328] leading-relaxed mb-6 font-light">
            Hay lugares que guardan una energía especial. 
          </p>
          
          <p className="text-lg text-[#2D3328]/70 leading-relaxed mb-6">
            Montañas, senderos, monasterios... espacios donde el velo 
            entre lo visible y lo invisible se hace más fino.
          </p>

          <p className="text-lg text-[#2D3328]/70 leading-relaxed mb-10">
            Llevo años explorando estos lugares, caminándolos en silencio, 
            descubriendo sus secretos. Ahora te invito a recorrerlos conmigo.
          </p>

          <p className="text-[#c9a227] font-medium text-lg">
            Cada viaje es único. Cada grupo marca su propio ritmo.
          </p>
        </div>
      </section>

      {/* ========== VIAJES ========== */}
      <section id="viajes" className="py-20 lg:py-28 bg-[#f8f6f0]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-heading text-[#2D3328] mb-4">
              Experiencias disponibles
            </h2>
            <p className="text-[#2D3328]/60">
              Elige el viaje que resuena con tu momento actual
            </p>
          </div>

          <div className="space-y-8">
            {viajes.map((viaje) => (
              <div 
                key={viaje.id}
                className="bg-white rounded-3xl p-8 lg:p-10 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
                  {/* Left: Title & Info */}
                  <div className="lg:col-span-1">
                    <div className="flex items-start gap-4 mb-6">
                      <div className="w-14 h-14 bg-[#2D3328]/5 rounded-2xl flex items-center justify-center flex-shrink-0">
                        <viaje.icon className="w-7 h-7 text-[#c9a227]" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-heading text-[#2D3328]">{viaje.title}</h3>
                        <p className="text-[#c9a227] font-medium">{viaje.subtitle}</p>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-3 mb-6">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#f8f6f0] rounded-full text-sm text-[#2D3328]/70">
                        <Clock className="w-3.5 h-3.5" />
                        {viaje.duration}
                      </span>
                      <span className={`px-3 py-1.5 ${viaje.statusColor} rounded-full text-sm text-white`}>
                        {viaje.status}
                      </span>
                    </div>

                    <p className="text-sm text-[#2D3328]/50">
                      <strong className="text-[#2D3328]/70">Ideal para:</strong> {viaje.ideal}
                    </p>
                  </div>

                  {/* Right: Description & Highlights */}
                  <div className="lg:col-span-2">
                    <p className="text-[#2D3328]/80 leading-relaxed mb-6">
                      {viaje.description}
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                      {viaje.highlights.map((item, i) => (
                        <div key={i} className="flex items-center gap-2 text-[#2D3328]/70 text-sm">
                          <Sparkles className="w-4 h-4 text-[#c9a227] flex-shrink-0" />
                          {item}
                        </div>
                      ))}
                    </div>

                    {viaje.ctaSlug ? (
                      <div className="flex flex-wrap gap-3">
                        <a
                          href={`/retiros/${viaje.ctaSlug}`}
                          className="inline-flex items-center gap-2 bg-[#2D3328] hover:bg-[#3a4235] text-white px-6 py-2.5 rounded-md text-sm font-medium transition-colors"
                          data-testid={`acceder-${viaje.id}`}
                        >
                          Acceder al retiro
                          <ArrowRight className="w-4 h-4" />
                        </a>
                      </div>
                    ) : (
                      <Button
                        onClick={() => scrollToForm(viaje.title)}
                        className="bg-[#2D3328] hover:bg-[#3a4235] text-white"
                        data-testid={`solicitar-${viaje.id}`}
                      >
                        Solicitar información
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== PHILOSOPHY ========== */}
      <section className="py-20 lg:py-28 bg-[#2D3328]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="w-12 h-[2px] bg-[#c9a227] mx-auto mb-10" />
            <h2 className="text-3xl lg:text-4xl font-heading text-white mb-4">
              Mi forma de acompañar
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#c9a227]/10 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <Compass className="w-8 h-8 text-[#c9a227]" />
              </div>
              <h3 className="text-white font-heading text-lg mb-3">Sin programa rígido</h3>
              <p className="text-white/60 text-sm leading-relaxed">
                La energía del grupo marca el ritmo. Cada viaje es único e irrepetible.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-[#c9a227]/10 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <Users className="w-8 h-8 text-[#c9a227]" />
              </div>
              <h3 className="text-white font-heading text-lg mb-3">Grupos reducidos</h3>
              <p className="text-white/60 text-sm leading-relaxed">
                Para sostener el espacio y acompañar cada proceso con presencia.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-[#c9a227]/10 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <Heart className="w-8 h-8 text-[#c9a227]" />
              </div>
              <h3 className="text-white font-heading text-lg mb-3">Experiencia viva</h3>
              <p className="text-white/60 text-sm leading-relaxed">
                No es turismo espiritual. Es un encuentro real con lugares de poder.
              </p>
            </div>
          </div>

          <div className="mt-16 text-center">
            <blockquote className="text-xl lg:text-2xl text-white/80 font-light italic max-w-2xl mx-auto">
              "El verdadero viaje no es llegar a un destino, 
              <span className="block text-[#c9a227] not-italic mt-2">es descubrir quién eres mientras caminas."</span>
            </blockquote>
          </div>
        </div>
      </section>

      {/* ========== FORM ========== */}
      <section id="solicitud-form" className="py-20 lg:py-28 bg-white">
        <div className="max-w-xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="w-12 h-[2px] bg-[#c9a227] mx-auto mb-10" />
            <h2 className="text-3xl lg:text-4xl font-heading text-[#2D3328] mb-4">
              Solicita información
            </h2>
            <p className="text-[#2D3328]/60">
              Te prepararé una propuesta personalizada
            </p>
          </div>

          {submitted ? (
            <div className="bg-[#f8f6f0] rounded-3xl p-10 text-center">
              <div className="w-16 h-16 bg-[#c9a227]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Heart className="w-8 h-8 text-[#c9a227]" />
              </div>
              <h3 className="text-2xl font-heading text-[#2D3328] mb-4">
                ¡Gracias por tu interés!
              </h3>
              <p className="text-[#2D3328]/70">
                Me pondré en contacto contigo pronto para 
                preparar una propuesta personalizada.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-[#2D3328]/70">Nombre *</label>
                <Input
                  type="text"
                  placeholder="Tu nombre"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-[#f8f6f0] border-[#2D3328]/10 focus:border-[#c9a227] py-5"
                  data-testid="viajes-form-name"
                />
              </div>
              
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-[#2D3328]/70">Email *</label>
                <Input
                  type="email"
                  placeholder="tu@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="bg-[#f8f6f0] border-[#2D3328]/10 focus:border-[#c9a227] py-5"
                  data-testid="viajes-form-email"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-[#2D3328]/70">
                  WhatsApp <span className="text-[#2D3328]/40">(opcional)</span>
                </label>
                <Input
                  type="tel"
                  placeholder="+34 600 000 000"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="bg-[#f8f6f0] border-[#2D3328]/10 focus:border-[#c9a227] py-5"
                  data-testid="viajes-form-phone"
                />
              </div>
              
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-[#2D3328]/70">Viaje de interés</label>
                <select
                  value={formData.viaje}
                  onChange={(e) => setFormData({ ...formData, viaje: e.target.value })}
                  className="w-full bg-[#f8f6f0] border border-[#2D3328]/10 focus:border-[#c9a227] rounded-md py-3 px-4 text-[#2D3328]"
                  data-testid="viajes-form-select"
                >
                  <option value="">Selecciona una opción</option>
                  <option value="Montserrat">Montserrat - Peregrinaje Interior</option>
                  <option value="REINICIA">REINICIA - Retiro en Teruel</option>
                  <option value="Camino de Santiago">Camino de Santiago</option>
                  <option value="Otro">Otro / No estoy seguro</option>
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-[#2D3328]/70">
                  Cuéntame más <span className="text-[#2D3328]/40">(opcional)</span>
                </label>
                <Textarea
                  placeholder="¿Qué te llama de este viaje? ¿Tienes fechas en mente?"
                  value={formData.mensaje}
                  onChange={(e) => setFormData({ ...formData, mensaje: e.target.value })}
                  className="bg-[#f8f6f0] border-[#2D3328]/10 focus:border-[#c9a227] min-h-[100px]"
                  data-testid="viajes-form-mensaje"
                />
              </div>

              <div className="flex items-start gap-3 pt-2">
                <Checkbox
                  id="viajes-consent"
                  checked={formData.consent}
                  onCheckedChange={(checked) => setFormData({ ...formData, consent: checked })}
                  className="mt-0.5"
                  data-testid="viajes-form-consent"
                />
                <label 
                  htmlFor="viajes-consent" 
                  className="text-sm text-[#2D3328]/60 cursor-pointer leading-relaxed"
                >
                  Acepto el tratamiento de mis datos para gestionar mi solicitud conforme a la{' '}
                  <a href="/privacidad" target="_blank" rel="noopener noreferrer" className="text-[#2D3328] underline hover:no-underline">Política de privacidad</a>.
                </label>
              </div>

              <Button 
                type="submit" 
                className="w-full py-6 text-base bg-[#2D3328] hover:bg-[#3a4235] text-white"
                disabled={submitting}
                data-testid="viajes-form-submit"
              >
                {submitting ? 'Enviando...' : 'Solicitar propuesta'}
              </Button>
            </form>
          )}
        </div>
      </section>

      {/* ========== FINAL CTA ========== */}
      <section className="py-12 bg-[#f8f6f0] border-t border-[#2D3328]/5">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-[#2D3328]/60 mb-3">
            ¿Prefieres hablar antes de decidir?
          </p>
          <a 
            href="https://wa.me/34654827313"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[#c9a227] hover:text-[#b8922a] font-medium transition-colors"
          >
            Escríbeme por WhatsApp
            <Send className="w-4 h-4" />
          </a>
        </div>
      </section>

    </div>
  );
};

export default ViajesIniciaticos;
